import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMyPayments, getMemberDetail, getServices, getMyReminders, dismissMyReminder, getMyContributionSummary } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { FiDollarSign, FiClock, FiCheckCircle, FiXCircle, FiActivity, FiHeart, FiBell, FiX } from 'react-icons/fi';
import useAppRefresh from '../../hooks/useAppRefresh';

const statusPill = { pending: 'pill-yellow', approved: 'pill-green', rejected: 'pill-red' };

const ACTIVITY_DAYS = 90;
const FOLLOWUP_ON_TRACK = 60;
const FOLLOWUP_OVERDUE = 120;

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const MemberDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [memberDetail, setMemberDetail] = useState(null);
  const [recentServices, setRecentServices] = useState([]);
  const [reminders, setReminders] = useState([]);
  // v1.8.1 — read the contribution status so we can hide the top-right
  // "Submit Payment" quick-action whenever the persistent debt banner is
  // already showing a "Pay now" CTA (overdue / due-soon). When status is
  // up_to_date or none, the banner has no button so we keep this one.
  const [contribStatus, setContribStatus] = useState(null);

  const loadReminders = () => getMyReminders().then(r => setReminders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  const loadAll = () => {
    getMyPayments().then(r => setPayments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    if (user?.member_id) getMemberDetail(user.member_id).then(r => setMemberDetail(r.data || null)).catch(() => {});
    getServices({ from: daysAgo(ACTIVITY_DAYS) }).then(r => setRecentServices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    loadReminders();
    getMyContributionSummary().then(r => setContribStatus(r.data?.status || null)).catch(() => {});
  };
  useAppRefresh(loadAll);

  useEffect(() => {
    getMyPayments().then(r => setPayments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    if (user?.member_id) {
      getMemberDetail(user.member_id).then(r => setMemberDetail(r.data || null)).catch(() => {});
    }
    getServices({ from: daysAgo(ACTIVITY_DAYS) })
      .then(r => setRecentServices(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    loadReminders();
    getMyContributionSummary().then(r => setContribStatus(r.data?.status || null)).catch(() => {});
    const refreshContrib = () => getMyContributionSummary().then(r => setContribStatus(r.data?.status || null)).catch(() => {});
    window.addEventListener('subscription-updated', refreshContrib);
    return () => window.removeEventListener('subscription-updated', refreshContrib);
  }, [user?.member_id]);

  const dismissReminder = async (id) => {
    try { await dismissMyReminder(id); loadReminders(); } catch {}
  };

  const pending = payments.filter(p => p.status === 'pending').length;
  const approved = payments.filter(p => p.status === 'approved').length;
  const rejected = payments.filter(p => p.status === 'rejected').length;
  const totalApproved = payments.filter(p => p.status === 'approved').reduce((s, p) => s + parseFloat(p.amount_usd || 0), 0);

  const cutoffDate = daysAgo(ACTIVITY_DAYS);
  const totalServices = recentServices.length;
  const attendedCount = (memberDetail?.attendance || []).filter(a => a.date >= cutoffDate).length;
  const attendancePct = totalServices > 0 ? Math.round((attendedCount / totalServices) * 100) : null;

  let activityStatus, activityColor, activityBg;
  if (totalServices === 0) {
    activityStatus = t('memberDashboard.justJoined'); activityColor = '#6b7280'; activityBg = '#f3f4f6';
  } else if (attendancePct >= 50) {
    activityStatus = t('common.active'); activityColor = '#16a34a'; activityBg = '#dcfce7';
  } else if (attendancePct >= 25) {
    activityStatus = t('memberDashboard.moderate'); activityColor = '#a16207'; activityBg = '#fef9c3';
  } else {
    activityStatus = t('memberDashboard.low'); activityColor = '#dc2626'; activityBg = '#fee2e2';
  }

  const lastFollowup = memberDetail?.followups?.[0]?.date || null;
  const daysSinceFollowup = lastFollowup
    ? Math.floor((Date.now() - new Date(lastFollowup).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  let followupStatus, followupColor, followupBg, followupDetail;
  if (daysSinceFollowup === null) {
    followupStatus = t('memberDashboard.noFollowupYet'); followupColor = '#6b7280'; followupBg = '#f3f4f6';
    followupDetail = t('memberDashboard.noVisitYet');
  } else if (daysSinceFollowup <= FOLLOWUP_ON_TRACK) {
    followupStatus = t('memberDashboard.onTrack'); followupColor = '#16a34a'; followupBg = '#dcfce7';
    followupDetail = t('memberDashboard.lastVisit', { days: daysSinceFollowup });
  } else if (daysSinceFollowup <= FOLLOWUP_OVERDUE) {
    followupStatus = t('followup.overdue'); followupColor = '#a16207'; followupBg = '#fef9c3';
    followupDetail = t('memberDashboard.lastVisit', { days: daysSinceFollowup });
  } else {
    followupStatus = t('memberDashboard.longOverdue'); followupColor = '#dc2626'; followupBg = '#fee2e2';
    followupDetail = t('memberDashboard.lastVisit', { days: daysSinceFollowup });
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>{user?.name}</h1>
        </div>
        {contribStatus !== 'overdue' && contribStatus !== 'due_soon' && (
          <button className="btn btn-primary" onClick={() => navigate('/member/submit-payment')}>
            <FiDollarSign /> {t('nav.submitPayment')}
          </button>
        )}
      </div>

      {/* Payment reminders banner */}
      {reminders.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {reminders.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 14, marginBottom: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#fef3c7', color: '#a16207', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FiBell />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e' }}>Payment reminder — {r.fund_name || 'contribution'}</div>
                {r.message && <div style={{ fontSize: 12, color: '#78350f', marginTop: 4, whiteSpace: 'pre-wrap' }}>{r.message}</div>}
                <div style={{ fontSize: 11, color: '#a16207', marginTop: 6 }}>
                  Sent {r.sent_at?.slice(0, 16)}{r.sent_by_name ? ` by ${r.sent_by_name}` : ''}
                </div>
              </div>
              <button className="btn-icon" onClick={() => dismissReminder(r.id)} title="Dismiss"><FiX /></button>
            </div>
          ))}
        </div>
      )}

      {/* Activity cards (member + spiritual father follow-up) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 16 }}>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: activityBg, color: activityColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
              <FiActivity />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{t('memberDashboard.yourActivity')}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: activityColor }}>{activityStatus}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {totalServices === 0
                  ? t('memberDashboard.noServicesInPeriod')
                  : t('memberDashboard.attendedSummary', { count: attendedCount, total: totalServices, pct: attendancePct })}
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: followupBg, color: followupColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
              <FiHeart />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{t('memberDashboard.spiritualFatherVisits')}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: followupColor }}>{followupStatus}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{followupDetail}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="stat-cards">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef9c3', color: '#a16207' }}><FiClock /></div>
          <div><div className="stat-label">{t('subscriptions.due')}</div><div className="stat-value">{pending}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}><FiCheckCircle /></div>
          <div><div className="stat-label">{t('subscriptions.paid')}</div><div className="stat-value">{approved}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fee2e2', color: '#dc2626' }}><FiXCircle /></div>
          <div><div className="stat-label">{t('memberDashboard.rejected')}</div><div className="stat-value">{rejected}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dbeafe', color: '#2563eb' }}><FiDollarSign /></div>
          <div><div className="stat-label">{t('common.total')} $</div><div className="stat-value">{totalApproved.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
        </div>
      </div>

      {/* Recent payments */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
          {t('memberDashboard.recentSubmissions')}
          <button className="btn btn-sm btn-secondary" onClick={() => navigate('/member/my-payments')}>{t('dashboard.viewAll')}</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead><tr><th>#</th><th>{t('common.date')}</th><th>{t('table.type')}</th><th>{t('finance.amountUSD')}</th><th>{t('finance.amountKwacha')}</th><th>{t('memberDashboard.mode')}</th><th>{t('common.status')}</th></tr></thead>
            <tbody>
              {payments.slice(0, 5).length === 0 ? (
                <tr><td colSpan={7}><div className="empty-state"><p>{t('memberDashboard.noSubmissions')}</p></div></td></tr>
              ) : payments.slice(0, 5).map((p, i) => (
                <tr key={p.id}>
                  <td>{i + 1}</td>
                  <td>{p.submitted_at?.slice(0, 10)}</td>
                  <td>{p.fund_name || '—'}</td>
                  <td>{parseFloat(p.amount_usd) > 0 ? `$${parseFloat(p.amount_usd).toFixed(2)}` : '—'}</td>
                  <td>{parseFloat(p.amount_kwacha) > 0 ? `K${parseFloat(p.amount_kwacha).toFixed(2)}` : '—'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{p.payment_method}</td>
                  <td>
                    <span className={`pill ${statusPill[p.status]}`}>{p.status}</span>
                    {p.status === 'rejected' && p.rejection_reason && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>{p.rejection_reason}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MemberDashboard;
